import React, { useState } from 'react'
import { downloadZipFile, generateSalesMaterialUtil, MediaType } from '../../utils/mediaGenerator'
import { Button } from '../../../../common'
import { Association, Booklet, Team } from '../../../../types/generated.types'
import { BsCircleFill, BsArrowRepeat } from 'react-icons/bs'
import JSZip from 'jszip'
import { copyZipFilesToMaster } from '../../association/resources/helpers/zipHelpers'
import { generateMobileImages } from '../../association/resources/helpers/mobileImagesGenerator'
import { buildBookletFolders } from '../../utils/bookletFolderUtils'
import ResourceSection from '../../ResourceSection'

interface SalesMaterialDownloaderProps {
  association: Association
  associationBooklets: Booklet[]
  team: Team
  generateSalesMaterialMutation: any
  generatingMediaType: MediaType | null
  setGeneratingMediaType: React.Dispatch<React.SetStateAction<MediaType | null>>
}

interface DownloadStatusIconProps {
  type: MediaType
  currentDownload: MediaType | null
}

const SalesMaterialDownloader: React.FC<SalesMaterialDownloaderProps> = ({
  association,
  associationBooklets,
  team,
  generateSalesMaterialMutation,
  generatingMediaType,
  setGeneratingMediaType
}) => {
  const [currentDownload, setCurrentDownload] = useState<MediaType | null>(null)

  const handleGenerateAndDownload = async () => {
    setGeneratingMediaType(MediaType.POSTER)
    const masterZip = new JSZip()
    const bookletFolders = buildBookletFolders(associationBooklets)

    try {
      // 1. Affisch
      setCurrentDownload(MediaType.POSTER)
      try {
        const { zip: posterZip } = await generateSalesMaterialUtil(
          associationBooklets,
          association._id,
          MediaType.POSTER,
          generateSalesMaterialMutation,
          undefined, // posterType
          team._id // teamId
        )
        await copyZipFilesToMaster(posterZip, masterZip, 'Säljmaterial/Affisch/', {
          bookletFolders
        })
      } catch (error) {
        console.error('Error generating posters:', error)
      }

      // 2. Sociala medier
      setCurrentDownload(MediaType.SOCIAL_MEDIA)
      try {
        const { zip: socialZip } = await generateSalesMaterialUtil(
          associationBooklets,
          association._id,
          MediaType.SOCIAL_MEDIA,
          generateSalesMaterialMutation
        )
        await copyZipFilesToMaster(socialZip, masterZip, 'Säljmaterial/Bilder till sociala medier/', {
          bookletFolders
        })
      } catch (error) {
        console.error('Error generating social media images:', error)
      }

      // 3. Mobilbilder
      setCurrentDownload(MediaType.MOBILE_IMAGES)
      try {
        const mobileZip = await generateMobileImages({
          associationBooklets
        })
        await copyZipFilesToMaster(mobileZip, masterZip, 'Säljmaterial/Mobilbilder/', {
          bookletFolders
        })
      } catch (error) {
        console.error('Error downloading mobile images:', error)
      }

      setCurrentDownload(null)
      await downloadZipFile(masterZip, `${association.name}-${team.name}-saljmaterial.zip`)
    } finally {
      setGeneratingMediaType(null)
      setCurrentDownload(null)
    }
  }

  return (
    <ResourceSection title='Säljmaterial'>
      <p>Här hittar du affischer och bilder som hjälper er att sprida er försäljning i sociala medier och på era aktiviteter.</p>
      <ul className="sales-material-list">
        <li>
          <DownloadStatusIcon
            type={MediaType.POSTER}
            currentDownload={currentDownload}
          />
          <span>Affisch</span>
        </li>
        <li>
          <DownloadStatusIcon
            type={MediaType.SOCIAL_MEDIA}
            currentDownload={currentDownload}
          />
          <span>Bilder till sociala medier</span>
        </li>
        <li>
          <DownloadStatusIcon
            type={MediaType.MOBILE_IMAGES}
            currentDownload={currentDownload}
          />
          <span>Mobilbilder</span>
        </li>
      </ul>
      <Button
        onClick={handleGenerateAndDownload}
        className='primary alt md'
        loading={generatingMediaType !== null}
        disabled={generatingMediaType !== null}
      >
        <div className="button-content">
          <span>Ladda ned säljmaterial</span>
        </div>
      </Button>
    </ResourceSection>
  )
}

const DownloadStatusIcon: React.FC<DownloadStatusIconProps> = ({ type, currentDownload }) => {
  if (currentDownload === type) {
    return (
      <div className="icon-container">
        <BsArrowRepeat className="spinner" />
      </div>
    )
  }
  return (
    <div className="icon-container">
      <BsCircleFill className="dot" />
    </div>
  )
}

export default SalesMaterialDownloader 
